import { Combobox } from '@base-ui/react/combobox'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { prepare as fuzzyPrepare } from 'fuzzysort'
import { ChevronDownIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useState } from 'react'
import type { DropdownChoice, DropdownChoiceId } from '@companion-app/shared/Model/Common.js'
import { Button } from '~/Components/Button'
import { DropdownInputPopup } from '~/Components/DropdownInputField/Popup.js'
import { useComboboxCollection } from '~/Components/DropdownInputField/useComboboxCollection.js'
import { InputGroup } from '~/Components/Form'
import { useComputed } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { fuzzyFilterSort } from '~/util/fuzzy'
// TODO: This CSS importing is a messy and needs refining
import '../Components/dropdown-field.css'
import './ButtonGridPanel.css'

interface ButtonGridHeaderProps {
	pageNumber: number
	changePage?: (delta: number) => void
	setPage?: (page: number) => void
	newPageAtEnd?: boolean
}

export const ButtonGridHeader = observer(function ButtonGridHeader({
	pageNumber,
	changePage,
	setPage,
	newPageAtEnd,
	children,
}: React.PropsWithChildren<ButtonGridHeaderProps>) {
	const { pages: pagesStore } = useContext(RootAppStoreContext)

	const pageOptions = useComputed(() => {
		const pageOptions: PageNumberOption[] = pagesStore.data.map((value, index) => ({
			value: index + 1,
			label: value.name ? `${index + 1} (${value.name})` : `${index + 1}`,
		}))

		if (newPageAtEnd) {
			pageOptions.push({
				value: -1,
				label: `[ Insert new page ]`,
			})
		}
		return pageOptions
	}, [pagesStore, newPageAtEnd])

	return (
		<PageNumberPicker pageNumber={pageNumber} changePage={changePage} setPage={setPage} pageOptions={pageOptions}>
			{children}
		</PageNumberPicker>
	)
})

export interface PageNumberOption {
	value: number
	label: string
}
interface PageNumberPickerProps {
	pageNumber: number
	changePage?: (delta: number) => void
	setPage?: (page: number) => void
	pageOptions: PageNumberOption[]
}

export const PageNumberPicker = observer(function ButtonGridHeader({
	pageNumber,
	changePage,
	setPage,
	pageOptions,
	children,
}: React.PropsWithChildren<PageNumberPickerProps>) {
	const inputChange = useCallback(
		(val: DropdownChoiceId | null) => {
			if (typeof val === 'number' && setPage && !isNaN(val)) {
				setPage(val)
			}
		},
		[setPage]
	)

	const nextPage = useCallback(() => changePage?.(1), [changePage])
	const prevPage = useCallback(() => changePage?.(-1), [changePage])

	const choiceOptions = useComputed<PageChoice[]>(() => {
		const options = pageOptions.map((o) => ({ id: o.value, label: o.label, fuzzy: fuzzyPrepare(o.label) }))
		if (!options.some((o) => o.id === pageNumber)) {
			const label = String(pageNumber)
			options.push({ id: pageNumber, label, fuzzy: fuzzyPrepare(label) })
		}
		return options
	}, [pageOptions, pageNumber])

	const collection = useComboboxCollection(choiceOptions, getEmptyLabel)

	const [inputValue, setInputValue] = useState('')

	const filteredItems = useComputed<PageChoice[]>(() => {
		if (!inputValue) return choiceOptions
		return fuzzyFilterSort(choiceOptions, inputValue)
	}, [choiceOptions, inputValue])

	return (
		<div className="button-grid-header">
			<InputGroup>
				<Button color="dark" hidden={!changePage} onClick={prevPage}>
					<FontAwesomeIcon icon={faChevronLeft} />
				</Button>
				<div className="dropdown-field button-page-input">
					<Combobox.Root<DropdownChoiceId | null, false, PageChoice>
						autoHighlight
						value={pageNumber}
						items={collection}
						filteredItems={filteredItems}
						disabled={!setPage}
						onValueChange={inputChange}
						onInputValueChange={setInputValue}
						itemToStringLabel={getEmptyLabel}
					>
						<Combobox.InputGroup className="form-input dropdown-field-input-group rounded-none">
							<Combobox.Input
								className="dropdown-field-input"
								placeholder={choiceOptions.find((o) => o.id === pageNumber)?.label ?? String(pageNumber ?? '')}
							/>
							<Combobox.Trigger className="dropdown-field-trigger">
								<ChevronDownIcon className="dropdown-field-icon" />
							</Combobox.Trigger>
						</Combobox.InputGroup>
						<DropdownInputPopup />
					</Combobox.Root>
				</div>
				<Button color="dark" hidden={!changePage} onClick={nextPage}>
					<FontAwesomeIcon icon={faChevronRight} />
				</Button>
			</InputGroup>
			<div className="right-buttons">{children}</div>
		</div>
	)
})

type PageChoice = DropdownChoice & { fuzzy: ReturnType<typeof fuzzyPrepare> }

// The input stays empty, with the current page shown as the placeholder
const getEmptyLabel = () => ''
